// Store Locator — per-branch URLs + WhatsApp — global storefront embed.
//
// Two client-side halves of the store-locator SEO feature (the server half is
// the seo-proxy + /api/seo; see docs/store-locator-seo-urls-spec.md):
//
//   1. Per-branch URLs — give every store card a real, shareable, crawlable link
//      to /store-locator/{slug}. The storefront serves ONE /store-locator page,
//      so clicking a card here updates the URL (history.pushState, no reload) and
//      focuses that store on the map; loading /store-locator/{slug} directly
//      focuses it. The seo-proxy injects the branch-specific <head> for that URL.
//   1b. Country views: /store-locator/namibia (any branch country, plus the
//      eswatini/swaziland alias) filters the list to that country's stores, fits
//      the map to them and shows a "Showing N stores in X / Show all stores" bar.
//      A branch slug always wins over a country slug.
//   2. WhatsApp (28-Sep feedback item 2) — the card markup has Tel/Email but no
//      WhatsApp; we add the WhatsApp link from /api/branches (whatsAppLink) to
//      each card that has one.
//
// Focus reuses the native "View Store" button (.sl-select-btn), which only pans
// the map + opens the info window — it does NOT change the shopping branch (see
// hide-head-office.js: "View Store is view-only"), so there's no reload loop.
//
// Runs only on /store-locator*; a clean no-op everywhere else and if the markup
// differs. NO storefront/theme change.

import { esc } from '../shared/dom.js';
import { apiJson } from '../shared/api.js';
import { branchSlug, buildBranchSlugMap, buildCountryMap } from '../shared/branchSlug.js';
import { installStoreLocatorAutocomplete } from '../shared/storeLocatorAutocomplete.js';

function escAttr(s) {
  if (s == null) return '';
  return String(s).replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/'/g, '&#39;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function cssEsc(s) {
  try { if (window.CSS && CSS.escape) return CSS.escape(s); } catch (e) { /* ignore */ }
  return String(s).replace(/["\\\]]/g, '\\$&');
}

function onStoreLocator() {
  try { return /^\/store-locator(\/|$)/i.test(location.pathname); } catch (e) { return false; }
}
function slugFromUrl() {
  try {
    var m = (location.pathname || '').match(/^\/store-locator\/([^/?#]+)\/?$/i);
    return m ? decodeURIComponent(m[1]).toLowerCase() : '';
  } catch (e) { return ''; }
}

// slug → branch (+ derived name→{slug,branch}) from /api/branches. `?all=1` keeps
// GW-531-hidden branches in the map only for name/whatsapp resolution; those
// don't get their own page server-side, but a card that still shows for them can
// at least render WhatsApp. Matching is by card data-name.
var _indexPromise = null;
function loadBranchIndex() {
  if (_indexPromise) return _indexPromise;
  _indexPromise = apiJson('/api/branches?all=1', null, []).then(function (list) {
    list = Array.isArray(list) ? list : [];
    var slugMap = buildBranchSlugMap(list);
    var byName = {};
    Object.keys(slugMap).forEach(function (slug) {
      var b = slugMap[slug];
      var nm = String((b && b.branchName) || '').trim().toUpperCase();
      if (nm) byName[nm] = { slug: slug, branch: b };
    });
    // Country → set of its branch slugs (cards are matched by data-pf-slug).
    var countries = buildCountryMap(list);
    var slugOfBranch = new Map();
    Object.keys(slugMap).forEach(function (slug) { slugOfBranch.set(slugMap[slug], slug); });
    Object.keys(countries).forEach(function (k) {
      var c = countries[k];
      if (c.slugSet) return; // alias entry shares the object
      c.slugSet = {};
      c.branches.forEach(function (b) { var sl = slugOfBranch.get(b); if (sl) c.slugSet[sl] = true; });
    });
    return { byName: byName, bySlug: slugMap, countries: countries };
  }).catch(function () { return { byName: {}, bySlug: {}, countries: {} }; });
  return _indexPromise;
}

function slugForCard(card, index) {
  var name = (card.getAttribute('data-name') || '').trim();
  if (!name) return { slug: '', branch: null };
  var hit = index.byName[name.toUpperCase()];
  return { slug: hit ? hit.slug : branchSlug(name), branch: hit ? hit.branch : null };
}

// Wrap the card title text in a real anchor to the branch URL (crawlable +
// middle-clickable). Idempotent.
function linkCardName(card, slug) {
  var nameEl = card.querySelector('.sl-card-name');
  if (!nameEl || nameEl.querySelector('a.pf-sl-permalink')) return;
  var text = nameEl.textContent;
  var a = document.createElement('a');
  a.className = 'pf-sl-permalink';
  a.setAttribute('href', '/store-locator/' + encodeURIComponent(slug));
  a.textContent = text;
  nameEl.textContent = '';
  nameEl.appendChild(a);
}

var WA_SVG = '<svg class="pf-sl-wa-icon" width="14" height="14" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" style="vertical-align:-2px;margin-right:4px"><path d="M12.04 2C6.58 2 2.13 6.45 2.13 11.91c0 1.75.46 3.45 1.32 4.95L2 22l5.25-1.38a9.9 9.9 0 0 0 4.79 1.22h.01c5.46 0 9.91-4.45 9.91-9.91 0-2.65-1.03-5.14-2.9-7.01A9.82 9.82 0 0 0 12.04 2Zm5.8 14.13c-.24.68-1.42 1.32-1.95 1.36-.53.04-1.02.24-3.44-.72-2.9-1.14-4.74-4.1-4.88-4.29-.14-.19-1.17-1.56-1.17-2.97 0-1.41.74-2.11 1-2.4.26-.29.57-.36.76-.36l.55.01c.18 0 .42-.07.65.5.24.58.82 2 .89 2.14.07.14.12.31.02.5-.1.19-.14.31-.29.48-.14.17-.3.38-.43.51-.14.14-.29.29-.12.57.17.29.74 1.22 1.59 1.98 1.09.97 2.01 1.27 2.29 1.42.29.14.45.12.62-.07.17-.19.72-.84.91-1.13.19-.29.38-.24.65-.14.26.09 1.68.79 1.97.93.29.14.48.22.55.34.07.12.07.7-.17 1.38Z"/></svg>';

// Add the WhatsApp line to a card's contact block. Idempotent.
function injectWhatsApp(card, branch) {
  if (!branch || card.querySelector('.pf-sl-whatsapp')) return;
  // Only render WhatsApp when the branch has an explicit whatsAppLink — no
  // fallback synthesised from whatsAppNumber. No link → no WhatsApp shown.
  var link = String(branch.whatsAppLink || '').trim();
  if (!link) return;
  var contact = card.querySelector('.sl-card-contact');
  if (!contact) return;
  var row = document.createElement('div');
  row.className = 'pf-sl-whatsapp';
  // Link text is always "Chat" — never the WhatsApp number.
  row.innerHTML = 'WhatsApp: <a href="' + escAttr(link) + '" target="_blank" rel="noopener" class="sl-whatsapp">' + WA_SVG + 'Chat</a>';
  contact.appendChild(row);
  try { card.setAttribute('data-whatsapp', link); } catch (e) { /* ignore */ }
}

function processCards(index) {
  var cards = document.querySelectorAll('.sl-card');
  for (var i = 0; i < cards.length; i++) {
    var card = cards[i];
    var res = slugForCard(card, index);
    if (!res.slug) continue;
    if (card.getAttribute('data-pf-slug') !== res.slug) card.setAttribute('data-pf-slug', res.slug);
    try { linkCardName(card, res.slug); } catch (e) { /* ignore */ }
    try { injectWhatsApp(card, res.branch); } catch (e) { /* ignore */ }
  }
}

// Add a WhatsApp line to the map info-window (the popup the native store locator
// builds from card data — .sl-info-window > .sl-iw-body with .sl-iw-contact rows
// for phone/email). It's rebuilt each time a branch is focused, so we re-scan on
// mutations and guard each popup with data-pf-wa. Branch matched by the popup's
// .sl-iw-name (branch name) against /api/branches.
function injectInfoWindowWhatsApp(iw, index) {
  if (!iw || iw.getAttribute('data-pf-wa') === '1') return;
  var nameEl = iw.querySelector('.sl-iw-name');
  var name = nameEl ? (nameEl.textContent || '').trim() : '';
  if (!name) return;
  var body = iw.querySelector('.sl-iw-body');
  if (!body) return;
  var hit = index.byName[name.toUpperCase()];
  var branch = hit ? hit.branch : null;
  // Mark processed regardless — a branch with no WhatsApp shouldn't be re-scanned.
  iw.setAttribute('data-pf-wa', '1');
  if (!branch) return;
  // Only render WhatsApp when the branch has an explicit whatsAppLink — no
  // fallback synthesised from whatsAppNumber. No link → no WhatsApp shown.
  var link = String(branch.whatsAppLink || '').trim();
  if (!link) return;
  var row = document.createElement('div');
  // Match the native contact-row markup (.sl-iw-contact + a Font Awesome icon) so
  // it inherits the popup's existing styling; fa-whatsapp is in the FA build the
  // page already loads (same as fa-phone / fa-envelope).
  row.className = 'sl-iw-contact pf-iw-whatsapp';
  // Link text is always "Chat" — never the WhatsApp number.
  row.innerHTML = '<i class="fa fa-whatsapp" aria-hidden="true"></i> <a href="' + escAttr(link) + '" target="_blank" rel="noopener">Chat</a>';
  // Sit with the other contacts, just before the Get Directions footer.
  var footer = body.querySelector('.sl-iw-footer');
  if (footer) body.insertBefore(row, footer); else body.appendChild(row);
}

function processInfoWindows(index) {
  var iws = document.querySelectorAll('.sl-info-window');
  for (var i = 0; i < iws.length; i++) {
    try { injectInfoWindowWhatsApp(iws[i], index); } catch (e) { /* ignore */ }
  }
}

function pushSlug(slug) {
  try {
    var url = '/store-locator/' + encodeURIComponent(slug);
    if (location.pathname !== url) history.pushState(null, '', url);
  } catch (e) { /* ignore */ }
}

// Focus a card on the map: scroll it into view + fire the native "View Store"
// (map-pan/info-window only). Never called from the select-btn handler, so no loop.
function focusCard(card) {
  if (!card) return;
  try { card.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch (e) { /* ignore */ }
  var btn = card.querySelector('.sl-select-btn');
  if (btn) { try { btn.click(); } catch (e) { /* ignore */ } }
}

function cardForSlug(slug) {
  if (!slug) return null;
  return document.querySelector('.sl-card[data-pf-slug="' + cssEsc(slug) + '"]');
}

// Deep-link on load / popstate: focus the branch in the URL once its card is
// ready (cards + slugs are set after /api/branches resolves).
function tryFocusFromUrl(attempt) {
  var slug = slugFromUrl();
  if (!slug) { clearCountry(); return; }
  var country = countryForSlug(slug);
  if (country) { if (_country !== country) tryCountryFromUrl(country, 0); return; }
  var card = cardForSlug(slug);
  if (card) { focusCard(card); return; }
  if ((attempt || 0) < 40) setTimeout(function () { tryFocusFromUrl((attempt || 0) + 1); }, 150);
}

// ── Country views (/store-locator/namibia) ──────────────────────────────────

var _index = null;          // resolved branch index (set in init)
var _country = null;        // active country entry, or null
var _suppressPush = false;  // true while we click a View Store button ourselves

function countryForSlug(slug) {
  if (!slug || !_index) return null;
  if (_index.bySlug && Object.prototype.hasOwnProperty.call(_index.bySlug, slug)) return null; // branch wins
  var c = _index.countries && _index.countries[slug];
  return c && c.slugSet ? c : null;
}

function ensureCountryStyles() {
  if (document.getElementById('pf-sl-country-css')) return;
  var st = document.createElement('style');
  st.id = 'pf-sl-country-css';
  // px, not rem: the storefront sets html{font-size:10px}.
  st.textContent =
    '.pf-sl-country{display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap;' +
    'margin:10px 10px 0;padding:10px 14px;border-radius:6px;background:#eef1fb;border:1px solid #d5dcf3;' +
    'color:#0a1a6e;font-size:14px;line-height:1.4}' +
    '.pf-sl-country strong{font-weight:700}' +
    '.pf-sl-country a{color:#d32f2f;font-weight:600;text-decoration:none;white-space:nowrap}' +
    '.pf-sl-country a:hover,.pf-sl-country a:focus{text-decoration:underline}';
  (document.head || document.documentElement).appendChild(st);
}

function removeCountryBar() {
  var bar = document.querySelector('.pf-sl-country');
  if (bar && bar.parentNode) bar.parentNode.removeChild(bar);
}

function showCountryBar(country, count) {
  removeCountryBar();
  var cards = document.querySelector('.sl-cards');
  if (!cards || !cards.parentNode) return;
  ensureCountryStyles();
  var bar = document.createElement('div');
  bar.className = 'pf-sl-country';
  bar.setAttribute('role', 'status');
  bar.innerHTML =
    '<span>Showing <strong>' + count + '</strong> Goldwagen ' + (count === 1 ? 'store' : 'stores') +
    ' in <strong>' + esc(country.name) + '</strong></span>' +
    '<a href="/store-locator" class="pf-sl-country-all">Show all stores</a>';
  cards.parentNode.insertBefore(bar, cards);
}

function clearCountry() {
  if (!_country) return;
  _country = null;
  removeCountryBar();
  var cards = document.querySelectorAll('.sl-card');
  for (var i = 0; i < cards.length; i++) {
    if (cards[i].getAttribute('data-pf-country-hidden') === '1') {
      cards[i].style.display = '';
      cards[i].removeAttribute('data-pf-country-hidden');
    }
  }
}

// The store locator keeps its google.maps.Map private. Capture it by briefly
// wrapping the Map prototype and firing one card's native View Store (which pans
// the map); its info window is suppressed for that synthetic click. Returns the
// map or null (map not ready yet).
function captureMap(card) {
  var g = window.google && window.google.maps;
  var btn = card && card.querySelector('.sl-select-btn');
  if (!g || !g.Map || !btn) return null;
  var proto = g.Map.prototype;
  var names = ['panTo', 'setCenter', 'setZoom', 'fitBounds', 'panBy', 'setOptions'];
  var orig = {};
  var captured = null;
  names.forEach(function (n) {
    if (typeof proto[n] !== 'function') return;
    orig[n] = proto[n];
    proto[n] = function () { if (!captured) captured = this; return orig[n].apply(this, arguments); };
  });
  var iwProto = g.InfoWindow && g.InfoWindow.prototype;
  var iwOpen = iwProto && iwProto.open;
  if (iwOpen) iwProto.open = function () { /* suppressed during capture */ };
  _suppressPush = true;
  try { btn.click(); } catch (e) { /* ignore */ }
  _suppressPush = false;
  if (iwOpen) iwProto.open = iwOpen;
  names.forEach(function (n) { if (orig[n]) proto[n] = orig[n]; });
  return captured;
}

function fitMapToCards(cards, attempt) {
  var g = window.google && window.google.maps;
  var map = g ? captureMap(cards[0]) : null;
  if (!map) {
    if ((attempt || 0) < 40) setTimeout(function () { if (_country) fitMapToCards(cards, (attempt || 0) + 1); }, 250);
    return;
  }
  var pts = [];
  cards.forEach(function (c) {
    var lat = parseFloat(c.getAttribute('data-lat'));
    var lng = parseFloat(c.getAttribute('data-lng'));
    if (isFinite(lat) && isFinite(lng)) pts.push({ lat: lat, lng: lng });
  });
  if (!pts.length) return;
  try {
    if (pts.length === 1) {
      map.setCenter(pts[0]);
      map.setZoom(12);
    } else {
      var bounds = new g.LatLngBounds();
      pts.forEach(function (p) { bounds.extend(p); });
      map.fitBounds(bounds, 60);
    }
  } catch (e) { /* ignore */ }
}

function applyCountry(country) {
  _country = country;
  var all = document.querySelectorAll('.sl-card');
  var shown = [];
  for (var i = 0; i < all.length; i++) {
    var card = all[i];
    var inCountry = !!country.slugSet[card.getAttribute('data-pf-slug') || ''];
    if (inCountry) {
      if (card.getAttribute('data-pf-country-hidden') === '1') card.removeAttribute('data-pf-country-hidden');
      card.style.display = '';
      shown.push(card);
    } else if (card.style.display !== 'none') {
      card.style.display = 'none';
      card.setAttribute('data-pf-country-hidden', '1');
    }
  }
  showCountryBar(country, shown.length);
  var list = document.querySelector('.sl-cards');
  if (list) { try { list.scrollTop = 0; } catch (e) { /* ignore */ } }
  if (shown.length) fitMapToCards(shown, 0);
}

// Wait for the cards to carry their slugs, then filter to the country.
function tryCountryFromUrl(country, attempt) {
  var ready = document.querySelector('.sl-card[data-pf-slug]');
  if (ready) { applyCountry(country); return; }
  if ((attempt || 0) < 40) setTimeout(function () { tryCountryFromUrl(country, (attempt || 0) + 1); }, 150);
}

var _wired = false;
function wireDelegation() {
  if (_wired) return; _wired = true;
  // Card title permalink → in-page focus (no full reload), keep the real href for
  // crawlers / new-tab / middle-click.
  document.addEventListener('click', function (e) {
    var a = e.target && e.target.closest && e.target.closest('a.pf-sl-permalink');
    if (a) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      var card = a.closest('.sl-card');
      var slug = card && card.getAttribute('data-pf-slug');
      if (!slug) return;
      e.preventDefault();
      pushSlug(slug);
      focusCard(card);
      return;
    }
    // Country bar "Show all stores" → back to the full list in-page.
    var all = e.target && e.target.closest && e.target.closest('a.pf-sl-country-all');
    if (all) {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      e.preventDefault();
      clearCountry();
      try { if (location.pathname !== '/store-locator') history.pushState(null, '', '/store-locator'); } catch (er) { /* ignore */ }
      return;
    }
    // Native Reset shows every card again, so leave the country view with it.
    if (_country && e.target && e.target.closest && e.target.closest('.sl-reset-btn')) {
      clearCountry();
      try { if (location.pathname !== '/store-locator') history.pushState(null, '', '/store-locator'); } catch (er) { /* ignore */ }
      return;
    }
    // Native "View Store" → keep the URL in sync (native handler still pans).
    // Only for real user clicks: scripted ones (our map capture, the theme
    // addon's saved-branch activation) must not rewrite the URL.
    var btn = e.isTrusted && !_suppressPush && e.target && e.target.closest && e.target.closest('.sl-select-btn');
    if (btn) {
      var c = btn.closest('.sl-card');
      var s = c && c.getAttribute('data-pf-slug');
      if (s) pushSlug(s);
    }
  }, false);
  // Typing a search re-filters every card natively, which replaces the country view.
  document.addEventListener('keyup', function (e) {
    if (_country && e.target && e.target.closest && e.target.closest('.sl-search-input')) {
      _country = null;
      removeCountryBar();
      var hidden = document.querySelectorAll('.sl-card[data-pf-country-hidden]');
      for (var i = 0; i < hidden.length; i++) hidden[i].removeAttribute('data-pf-country-hidden');
    }
  }, true);
  window.addEventListener('popstate', function () { tryFocusFromUrl(0); });
}

var scheduled = false;
function schedule(index) {
  if (scheduled) return;
  scheduled = true;
  var run = function () {
    scheduled = false;
    try { processCards(index); } catch (e) { /* ignore */ }
    try { processInfoWindows(index); } catch (e) { /* ignore */ }
  };
  if (window.requestAnimationFrame) window.requestAnimationFrame(run); else setTimeout(run, 16);
}

// The theme's store-locator addon (ScriptResource) activates the visitor's saved
// branch on window load: it reads localStorage SelectedBranchName, hides every
// other card and clicks that card's View Store. On a /store-locator/{slug} link
// that overrides the branch or country in the URL. This bundle is deferred, so
// it runs before load: for that one load dispatch, report no saved branch to it.
// Storage itself is untouched and the plain /store-locator page keeps the
// show-my-branch behaviour.
function shieldSavedBranchOnLoad() {
  if (!slugFromUrl() || document.readyState === 'complete') return;
  var proto;
  try { proto = window.Storage && Storage.prototype; } catch (e) { return; }
  if (!proto || typeof proto.getItem !== 'function') return;
  var orig = proto.getItem;
  proto.getItem = function (key) {
    if (key === 'SelectedBranchName' && this === window.localStorage) return null;
    return orig.apply(this, arguments);
  };
  var restore = function () { if (proto.getItem !== orig) proto.getItem = orig; };
  // After every load listener has run (theirs included), put getItem back.
  window.addEventListener('load', function () { setTimeout(restore, 0); });
  setTimeout(restore, 30000); // safety net if load never fires
}

export function init() {
  if (!onStoreLocator()) return;
  shieldSavedBranchOnLoad();
  loadBranchIndex().then(function (index) {
    _index = index;
    try { processCards(index); } catch (e) { /* ignore */ }
    try { processInfoWindows(index); } catch (e) { /* ignore */ }
    wireDelegation();
    tryFocusFromUrl(0);
    // Cards can render/filter late — re-process on mutations (coalesced).
    try {
      new MutationObserver(function () { schedule(index); }).observe(document.documentElement || document.body, { childList: true, subtree: true });
    } catch (e) { /* ignore */ }
  });
}

export var id = 'store-locator-urls';

// Runs at bundle load, not in init(): the native store locator builds its Google
// autocomplete on DOM ready, so the country restriction must be in place first.
if (onStoreLocator()) {
  try { installStoreLocatorAutocomplete(); } catch (e) { /* ignore */ }
}
